import { loadStripe, type Stripe, type StripeConstructorOptions } from '@stripe/stripe-js';
import { trackError, ErrorSeverity } from './error-tracking';

const STRIPE_SCRIPT_SELECTOR = 'script[src^="https://js.stripe.com/"]';
const STRIPE_HOST = 'js.stripe.com';
const STRIPE_PROBE_URL = 'https://js.stripe.com/v3/';

/** A failed first load gets one more try after this long; a dropped packet or a slow radio usually clears. */
export const STRIPE_RETRY_DELAY_MS = 1500;
/** How long the reachability probe waits before calling js.stripe.com unreachable. */
export const STRIPE_PROBE_TIMEOUT_MS = 5000;

/**
 * Why Stripe.js did not come up, in one word per cause:
 * - offline: the browser says it has no network.
 * - csp: a Content-Security-Policy refused the script (ours or one injected).
 * - http_<status>: Stripe answered, with an error status.
 * - blocked_before_network: refused inside the browser in under 50 ms, never
 *   reaching the network. An extension or browser shield.
 * - network_wide: other outside scripts failed too. A DNS filter, a school or
 *   office firewall, or a dead connection.
 * - stripe_unreachable: only Stripe failed and a direct request to it fails.
 *   A filter that names Stripe, or Stripe down for this network.
 * - reachable_but_failed: a direct request reaches Stripe, so the script was
 *   refused on the way in (a filter on script loads only) or the first load
 *   timed out.
 * - loaded_without_global: the script ran but never set window.Stripe; a
 *   blocker swapped in an empty stand-in.
 */
export type StripeLoadReason =
  | 'offline'
  | 'csp'
  | `http_${number}`
  | 'blocked_before_network'
  | 'network_wide'
  | 'stripe_unreachable'
  | 'reachable_but_failed'
  | 'loaded_without_global';

type Probe = 'reachable' | 'network_error' | 'timeout' | 'unsupported';

// A CSP refusal leaves nothing on the script's error event, so violations are
// collected from the moment this module loads (it ships with checkout).
const cspBlocked: string[] = [];
if (typeof document !== 'undefined') {
  document.addEventListener('securitypolicyviolation', (event) => {
    if (event.blockedURI.includes(STRIPE_HOST)) cspBlocked.push(event.effectiveDirective);
  });
}

/** Test seam: forget collected CSP violations. */
export function __resetStripeLoaderForTests(): void {
  cspBlocked.length = 0;
}

/** The newest Resource Timing entry for js.stripe.com, if the browser kept one. */
function stripeResourceEntry(): PerformanceResourceTiming | undefined {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return undefined;
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  return entries.filter((e) => e.name.includes(STRIPE_HOST)).pop();
}

/**
 * Hosts of other outside scripts that came back empty. A cached script still
 * has a decoded body, so an empty one failed. When these fail with Stripe the
 * cause is the buyer's network, not Stripe.
 */
function otherFailedScriptHosts(): string[] {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return [];
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const hosts = new Set<string>();
  for (const e of entries) {
    if (e.initiatorType !== 'script' || e.decodedBodySize > 0) continue;
    let host: string;
    try {
      host = new URL(e.name).host;
    } catch {
      continue;
    }
    if (host === window.location.host || host === STRIPE_HOST) continue;
    hosts.add(host);
  }
  return [...hosts].slice(0, 10);
}

/** A direct no-cors request to Stripe: tells a network that cannot reach it from one that only refuses scripts. */
async function probeStripe(): Promise<Probe> {
  if (typeof fetch !== 'function' || typeof AbortController === 'undefined') return 'unsupported';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), STRIPE_PROBE_TIMEOUT_MS);
  try {
    await fetch(STRIPE_PROBE_URL, { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
    return 'reachable';
  } catch {
    return controller.signal.aborted ? 'timeout' : 'network_error';
  } finally {
    clearTimeout(timer);
  }
}

interface Evidence {
  message: string;
  online: boolean;
  csp: string[];
  status: number | undefined;
  durationMs: number | undefined;
  otherFailed: string[];
  probe: Probe;
}

export function classifyStripeLoadFailure(e: Evidence): StripeLoadReason {
  if (e.message === 'Stripe.js not available') return 'loaded_without_global';
  if (!e.online) return 'offline';
  if (e.csp.length > 0) return 'csp';
  if (e.status !== undefined && e.status >= 400) return `http_${e.status}`;
  if (e.durationMs !== undefined && e.durationMs < 50 && !e.status) return 'blocked_before_network';
  if (e.otherFailed.length > 0) return 'network_wide';
  if (e.probe === 'network_error' || e.probe === 'timeout') return 'stripe_unreachable';
  return 'reachable_but_failed';
}

/**
 * What the page looked like when Stripe.js failed to come up, plus the one-word
 * reason. The script's error event carries nothing, so the reason is pieced
 * together from Resource Timing, CSP violations, the other outside scripts and
 * a direct request to Stripe.
 *
 * Browser-only: it runs from loadStripeSafely's catch, and on the server
 * loadStripe resolves null without throwing (stripe-js checks for a window).
 */
async function describeStripeLoadState(message: string): Promise<Record<string, unknown>> {
  // Everything on the page is read before the probe's first await: the retry
  // starts while the probe runs, and would otherwise add a second script tag
  // (or set window.Stripe) to a picture meant to show the first failure.
  const entry = stripeResourceEntry();
  const status = entry && 'responseStatus' in entry ? (entry as { responseStatus: number }).responseStatus : undefined;
  const durationMs = entry ? Math.round(entry.duration) : undefined;
  const otherFailed = otherFailedScriptHosts();
  const csp = [...cspBlocked];
  const page = {
    scriptTags: document.querySelectorAll(STRIPE_SCRIPT_SELECTOR).length,
    windowStripe: typeof (window as { Stripe?: unknown }).Stripe,
    online: navigator.onLine,
    // 2026-10-27: express-checkout rows from AWS addresses with a desktop
    // Chrome agent. These say whether a driven browser or a person on a slow
    // or hidden page failed to load it, which the agent alone cannot.
    visibility: document.visibilityState,
    msSinceLoad: Math.round(performance.now()),
    webdriver: navigator.webdriver === true,
    // 459 of 462 rows in October were one bot whose agent says Mac and whose
    // platform says Linux. Flagged here so a real buyer's row stands out.
    agentMismatch: /Macintosh|Windows/.test(navigator.userAgent) && /Linux/.test(navigator.platform),
  };
  const probe = await probeStripe();
  const evidence: Evidence = { message, online: page.online, csp, status, durationMs, otherFailed, probe };
  return {
    reason: classifyStripeLoadFailure(evidence),
    ...page,
    probe,
    httpStatus: status ?? null,
    durationMs: durationMs ?? null,
    protocol: entry?.nextHopProtocol || null,
    csp,
    otherFailed,
  };
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

/**
 * loadStripe, but a failed load is tried once more and then resolves to null
 * instead of rejecting.
 *
 * The promise goes straight into <Elements>, which only calls .then on it, so
 * a rejection became an unhandledrejection row with no context at all. Null is
 * what Elements already understands as "no Stripe": the form stays unmounted,
 * which is what a rejected load showed the buyer anyway.
 *
 * stripe-js forgets a failed load and injects a fresh script on the next call,
 * so the retry is a real second request, STRIPE_RETRY_DELAY_MS after the
 * first failed. A load saved by the retry is logged
 * as a warning with the same reason, so the rate of first-try failures stays
 * visible.
 */
export async function loadStripeSafely(
  publishableKey: string,
  options: StripeConstructorOptions | undefined,
  component: string,
): Promise<Stripe | null> {
  let first: Error;
  try {
    return await loadStripe(publishableKey, options);
  } catch (error) {
    first = toError(error);
  }
  // The diagnosis (up to STRIPE_PROBE_TIMEOUT_MS on a hung network) runs
  // during the wait, not before it, so the buyer's second try starts after
  // STRIPE_RETRY_DELAY_MS whatever the probe is doing. It is read after the
  // retry, when the row is written.
  const diagnosis = describeStripeLoadState(first.message);

  await wait(STRIPE_RETRY_DELAY_MS);
  try {
    const stripe = await loadStripe(publishableKey, options);
    const extra = await diagnosis;
    trackError(new Error(`Stripe.js loaded on retry: ${first.message}`), ErrorSeverity.WARNING, {
      component,
      action: 'loadStripe',
      extra,
    });
    return stripe;
  } catch (error) {
    const extra = await diagnosis;
    trackError(first, ErrorSeverity.ERROR, {
      component,
      action: 'loadStripe',
      extra: { ...extra, retry: toError(error).message },
    });
    return null;
  }
}
